import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../usercomponents/SideBar';
import TopNavBar from '../usercomponents/TopNavBar';
import Footer from '../usercomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function isPdf(filename) {
  return !!filename && filename.toLowerCase().endsWith('.pdf');
}

function isImage(filename) {
  return !!filename && /\.(jpe?g|png|gif|webp|bmp)$/i.test(filename);
}

function formatDateTime(v) {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StudentAssignmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const getToken = () => JSON.parse(sessionStorage.getItem('studentSession'))?.token;

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) { setError('Authentication required.'); setLoading(false); return; }
      const response = await axios.get(`${apiUrl}/api/student/assignments/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        setAssignment(response.data.assignment);
        setSubmission(response.data.submission);
        setAttachments(response.data.attachments || []);
      } else {
        setError(response.data.message || 'Failed to fetch assignment.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching assignment.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleView = () => {
    if (isPdf(submission.original_filename)) {
      navigate('/assignment-submission-pdf', {
        state: {
          submissionId: submission.id,
          filename: submission.original_filename,
          assignmentTitle: assignment?.title,
        },
      });
    } else {
      handleDownload();
    }
  };

  const handleDownload = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/student/assignments/submissions/${submission.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', submission.original_filename || 'submission');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error downloading your submitted file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!selectedFile) {
      setError('Please choose a PDF or Word document to upload.');
      return;
    }
    setSubmitting(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await axios.post(`${apiUrl}/api/student/assignments/${id}/submit`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        setSuccess('Your answer has been submitted.');
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchDetail();
      } else {
        setError(response.data.message || 'Failed to submit.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error submitting your answer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper">
          <TopNavBar />
          <div className="dashboard-body text-center py-40"><div className="spinner-border text-main-600" /></div>
          <Footer />
        </div>
      </>
    );
  }

  const deadlinePassed = assignment?.deadline && new Date(assignment.deadline) < new Date();
  const isGraded = !!submission?.reviewed_at;

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body px-3">
          <div className="breadcrumb mb-24">
            <ul className="d-flex align-items-center gap-2 list-unstyled">
              <li><Link to="/dashboard" className="text-gray-200 text-15 hover-text-main-600 text-decoration-none">Home</Link></li>
              <li><span className="text-gray-500 d-flex align-items-center"><i className="ph ph-caret-right" /></span></li>
              <li><Link to="/assignments" className="text-gray-200 text-15 hover-text-main-600 text-decoration-none">Assignments</Link></li>
              <li><span className="text-gray-500 d-flex align-items-center"><i className="ph ph-caret-right" /></span></li>
              <li><span className="text-main-600 text-15">{assignment?.title || 'Assignment'}</span></li>
            </ul>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {assignment && (
            <>
              <div className="card shadow-sm border-0 mb-20">
                <div className="card-body p-20">
                  <div className="fw-bold text-18 mb-4">{assignment.title}</div>
                  <div className="text-13 text-gray-500 mb-16">
                    {assignment.coursename}
                    {assignment.deadline && ` · Deadline: ${formatDateTime(assignment.deadline)}`}
                    {assignment.max_marks !== null && assignment.max_marks !== undefined && ` · Max marks: ${assignment.max_marks}`}
                  </div>
                  <div dangerouslySetInnerHTML={{ __html: assignment.question }} />

                  {attachments.length > 0 && (
                    <div className="mt-16 pt-16 border-top border-gray-100">
                      <div className="fw-semibold text-14 mb-8">Supporting Documents</div>
                      <div className="d-flex flex-wrap gap-12">
                        {attachments.map((att) => {
                          const url = `${apiUrl}/${att.file_path}`;
                          return (
                            <a
                              key={att.id}
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="d-inline-flex flex-column align-items-center text-decoration-none border border-gray-100 rounded-8 p-8"
                              style={{ width: 130 }}
                              title={att.original_name}
                            >
                              {isImage(att.original_name) ? (
                                <img
                                  src={url}
                                  alt={att.original_name}
                                  style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 6 }}
                                />
                              ) : (
                                <span
                                  className="d-flex align-items-center justify-content-center"
                                  style={{ width: '100%', height: 90, background: '#f4f6f8', borderRadius: 6 }}
                                >
                                  <i className={`ph ${isPdf(att.original_name) ? 'ph-file-pdf' : 'ph-file-text'} text-32 text-gray-400`} />
                                </span>
                              )}
                              <span className="text-12 text-gray-300 mt-6 text-truncate w-100 text-center">
                                {att.original_name}
                              </span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="card shadow-sm border-0 mb-20">
                <div className="card-body p-20">
                  <div className="fw-semibold mb-12">Your Submission</div>

                  {submission?.submitted_at && (
                    <div className="mb-16 p-12" style={{ background: '#f6faf8', borderRadius: 8 }}>
                      <div className="text-13 text-gray-500">Submitted {formatDateTime(submission.submitted_at)}</div>
                      <span
                        role="button"
                        className="text-main-600 text-decoration-underline text-14"
                        style={{ cursor: 'pointer' }}
                        onClick={handleView}
                      >
                        {isPdf(submission.original_filename) ? 'View' : 'Download'} {submission.original_filename || 'your submitted file'}
                      </span>
                      {isPdf(submission.original_filename) && (
                        <>
                          {' · '}
                          <span
                            role="button"
                            className="text-main-600 text-decoration-underline text-14"
                            style={{ cursor: 'pointer' }}
                            onClick={handleDownload}
                          >
                            Download
                          </span>
                        </>
                      )}
                    </div>
                  )}

                  {submission?.submitted_at ? (
                    isGraded ? (
                      <div>
                        <span className="badge bg-success-subtle text-success-emphasis text-14 mb-8">
                          Marks: {submission.marks}{assignment.max_marks !== null && assignment.max_marks !== undefined ? ` / ${assignment.max_marks}` : ''}
                        </span>
                        {submission.comment && (
                          <div className="mt-8">
                            <div className="text-13 text-gray-500 mb-4">Feedback from admin:</div>
                            <div className="p-12" style={{ background: '#fff8f0', borderRadius: 8 }}>{submission.comment}</div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="badge bg-warning-subtle text-warning-emphasis text-14">Review Pending</span>
                    )
                  ) : (
                    <div className="text-gray-400 mb-8">You have not submitted an answer yet.</div>
                  )}

                  {deadlinePassed ? (
                    <div className="alert alert-warning mt-16 mb-0">The deadline for this assignment has passed. You can no longer submit or change your answer.</div>
                  ) : (
                    <form onSubmit={handleSubmit} className="mt-16">
                      <label className="text-13 text-gray-500 mb-4 d-block">
                        {submission?.submitted_at ? 'Replace your submission (PDF or Word document)' : 'Upload your answer (PDF or Word document)'}
                      </label>
                      <div className="d-flex gap-8 flex-wrap">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          className="form-control py-9"
                          style={{ maxWidth: 320 }}
                          onChange={(e) => setSelectedFile(e.target.files[0] || null)}
                          disabled={submitting}
                        />
                        <button type="submit" className="btn btn-main rounded-pill py-9 px-20" disabled={submitting}>
                          {submitting ? 'Submitting...' : submission?.submitted_at ? 'Resubmit' : 'Submit Answer'}
                        </button>
                      </div>
                      {submission?.submitted_at && (
                        <div className="text-13 text-gray-500 mt-8">Resubmitting will replace your previous file{isGraded ? ' and clear the existing marks/comment' : ''}.</div>
                      )}
                    </form>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default StudentAssignmentDetail;
